import { themeConstants } from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch, useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  bulkInvitePeopleLocation,
  removeBulkInviteQueryParam,
} from 'src/app/BulkInvite/bulkInvite.routes'
import BulkInviteModal from 'src/app/BulkInvite/BulkInviteModal'
import PeopleInvitedToOrg from 'src/app/PeopleInvitedToOrg/PeopleInvitedToOrg'
import PeopleMembers from 'src/app/PeopleMembers/PeopleMembers'
import FamilyLayout, {
  FamilyLayoutTab,
} from 'src/component-layout/FamilyLayout/FamilyLayout'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell, {
  LayoutShellButton,
} from 'src/components/LayoutShell/LayoutShell'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useTranslate } from 'src/localization/Trns'
import { Rdrct } from 'src/navigation/Lnk'
import { peopleRouteTree } from 'src/navigation/peopleRouteTree'
import {
  invitePeopleUrl,
  peopleInvitedToOrgUrl,
  peopleMembersUrl,
  peopleUrl,
} from 'src/navigation/urls'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectPeopleInvitedToOrgMatch,
  selectPeopleMembersMatch,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

// Stable test ids per family tab — durable e2e selectors (legacy Subnav had
// none). Active tab also sets aria-current="page" via FamilyLayout.
const FAMILY_TAB_TEST_IDS: Record<string, string> = {
  members: 'peopleMembersTab',
  invited: 'peopleInvitedTab',
}

/**
 * PeopleFamilyPage — route-level page for the People family tabs. Parity
 * notes vs the legacy People Subnav: src/page-components/AGENTS.md.
 */
const PeopleFamilyPage: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const peopleMembersMatch = useSelector(selectPeopleMembersMatch)
  const peopleInvitedToOrgMatch = useSelector(selectPeopleInvitedToOrgMatch)
  const currentUser = useCurrentUser()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const bulkInviteCsvEnabled = useSelector(
    selectFeatureFlaggerFromState,
  ).bulk_invite_csv
  const queryParams = useSelector(selectQueryParams)
  // The bulk-invite modal layers over whichever People tab is active, deep-linked
  // via a query param so it survives refresh. Hosted here (not in a tab page) so
  // it overlays the origin tab and returns there on close. Gated on the
  // remote-config flag + admin access.
  const showBulkInvite =
    bulkInviteCsvEnabled &&
    Boolean(currentUser?.hasOrgAdminPermissions) &&
    queryParams.bulkInvite === '1'

  // Tab visibility copied from the legacy People Subnav; the whole bar is
  // gated on being in an explicit org.
  const isInExplicitOrg = !!currentUser?.isInExplicitOrg
  const tabVisibility: Record<string, boolean> = {
    members:
      isInExplicitOrg &&
      (!!organization?.memberListVisible ||
        !!currentUser?.hasOrgCardManagerPermissions ||
        !!currentUser?.hasOrgAdminPermissions),
    invited: isInExplicitOrg && !!currentUser?.hasOrgAdminPermissions,
  }

  const tabs: FamilyLayoutTab[] = useMemo(
    () =>
      peopleRouteTree.family.tabs
        .filter((tab) => tabVisibility[tab.key])
        .map((tab) => ({
          key: tab.key,
          label: translate(tab.labelSource),
          to: tab.path,
          ariaLabelSource: tab.labelSource,
          testId: FAMILY_TAB_TEST_IDS[tab.key],
        })),
    [
      isInExplicitOrg,
      organization?.memberListVisible,
      currentUser?.hasOrgCardManagerPermissions,
      currentUser?.hasOrgAdminPermissions,
      translate,
    ],
  )

  const activeTabKey =
    peopleMembersMatch !== null
      ? 'members'
      : peopleInvitedToOrgMatch !== null
        ? 'invited'
        : undefined

  const inviteAnalyticsEvent = peopleMembersMatch
    ? 'organization members - invite people - text link press'
    : peopleInvitedToOrgMatch
      ? 'invited to org - invite people - text link press'
      : 'invite people - text link press'

  const buttons: LayoutShellButton[] | undefined =
    currentUser?.hasOrgAdminPermissions
      ? [
          ...(bulkInviteCsvEnabled
            ? [
                <Button
                  key="bulkImport"
                  variant="secondary"
                  dropShadow={false}
                  testId="bulkInvitePeople"
                  aria-label={translate('Bulk Import')}
                  onClick={(): void => {
                    analytics?.track('people - bulk import - button press', {
                      intent: 'bulk invite',
                      interaction: 'button press',
                    })
                    history.push(
                      bulkInvitePeopleLocation(calculatedBackgroundLocation),
                    )
                  }}
                >
                  {translate('Bulk Import')}
                </Button>,
              ]
            : []),
          {
            className: 'people-invite-button--no-shadow',
            design: ButtonDesigns.designCta,
            type: ButtonTypes.button,
            text: translate('Invite People'),
            testId: 'invitePeople',
            linkProps: {
              to: invitePeopleUrl(),
              ariaLabel: 'Invite People',
            },
            onClick: (): void => {
              analytics?.track(inviteAnalyticsEvent, {
                intent: 'invite people',
                interaction: 'button press',
              })
            },
          },
        ]
      : undefined

  const familyContent = (
    <Switch location={calculatedBackgroundLocation}>
      <Route exact path={peopleMembersUrl()}>
        <PeopleMembers />
      </Route>
      <Route exact path={peopleInvitedToOrgUrl()}>
        <PeopleInvitedToOrg />
      </Route>
      <Route exact path={peopleUrl()}>
        <Rdrct to={peopleRouteTree.family.redirectTo} />
      </Route>
      <Route path={'/'}>
        <NotFoundBackgroundPageContent />
      </Route>
    </Switch>
  )

  return (
    <>
      <LayoutShell
        title={translate('People')}
        subnavDesign={SubnavDesign.Floating}
        subnavOptions={[]}
        buttons={buttons}
        css={css`
          .people-invite-button--no-shadow {
            box-shadow: inset 0 0 0 2px ${themeConstants.colors.cta_dark};

            &:hover {
              box-shadow: inset 0 0 0 2px ${themeConstants.colors.cta_dark};
            }
          }
        `}
        content={
          // Legacy parity: mount the tab bar only when >1 tab is visible; 0/1
          // tab renders content bare (no empty/lone bar).
          tabs.length > 1 ? (
            <FamilyLayout tabs={tabs} activeTabKey={activeTabKey}>
              {familyContent}
            </FamilyLayout>
          ) : (
            familyContent
          )
        }
      />
      {showBulkInvite && (
        <BulkInviteModal
          onClose={(): void =>
            history.push(removeBulkInviteQueryParam(history.location))
          }
        />
      )}
    </>
  )
}

export default PeopleFamilyPage
